/**
 * Getting a new release to people who already have the app.
 *
 * This is not cosmetic plumbing. The app is a PWA whose service worker serves the shell from its
 * own precache, so without an update path a returning user keeps the version they first
 * installed **forever** — a deploy reaches the server and nobody sees it. That is exactly what
 * happened before this module existed.
 *
 * Two signals, because the service-worker events alone are not enough:
 *
 *   1. `controllerchange` — a newer worker claimed this page. Fast, but easy to miss: with
 *      `skipWaiting` the new worker can activate and claim *before* the app's JavaScript runs,
 *      and an event that already fired cannot be listened for.
 *   2. **What is running versus what the server has.** The shell is served `no-cache`, so one
 *      `no-store` fetch of `/index.html` says which hashed entry bundle is current. If the
 *      document loaded a different one, this page is stale — whatever the worker's state is.
 *      This is the authoritative check, and it is what catches the case above.
 *
 * Reloading is then a judgement, not a reflex: signed out (the login screen) there is nothing to
 * lose, so the page reloads itself; signed in, a banner offers it, because nobody should lose a
 * half-written message to a deploy.
 */
const CHECK_EVERY_MS = 30 * 60_000;
const CHECK_MIN_GAP_MS = 60_000;
const RELOADED_KEY = 'tik.updatedTo';
/** Two, because the first reload can land before the new worker has taken control. */
const MAX_ATTEMPTS = 2;
const CLAIM_WAIT_MS = 3000;
const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

export interface UpdateHooks {
  /** True while a session exists — then an unannounced reload could lose a draft. */
  isSignedIn: () => boolean;
  reload?: () => void;
  now?: () => number;
  fetchText?: (url: string) => Promise<string>;
  /** The entry bundle this document actually loaded. */
  runningEntry?: () => string | null;
}

/** The `/assets/index-*.js` the given HTML points at, or null. */
export function entryOf(html: string): string | null {
  return html.match(ENTRY_RE)?.[0] ?? null;
}

function defaultRunningEntry(): string | null {
  if (typeof document === 'undefined') return null;
  for (const s of Array.from(document.scripts)) {
    const m = s.src.match(ENTRY_RE);
    if (m) return m[0];
  }
  return null;
}

class AppUpdate {
  /** A newer version is available and the page is running the old code. */
  ready = $state(false);

  private booted = false;
  private lastCheck = 0;
  private timer: ReturnType<typeof setInterval> | undefined;
  private hooks: UpdateHooks = { isSignedIn: () => false };

  boot(hooks: UpdateHooks): void {
    if (this.booted) return;
    this.booted = true;
    this.hooks = hooks;

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      // A page already controlled at boot has an older version installed; the first activation
      // of a fresh install is not an update.
      const wasControlled = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (wasControlled) void this.check(true);
      });
    }

    void this.check();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.check();
      });
    }
    this.timer = setInterval(() => void this.check(), CHECK_EVERY_MS);
  }

  /**
   * Ask the worker to look for a new version, then compare what the server serves with what
   * this page is running. Throttled to once a minute unless `force`.
   */
  async check(force = false): Promise<void> {
    const now = (this.hooks.now ?? Date.now)();
    if (!force && now - this.lastCheck < CHECK_MIN_GAP_MS) return;
    this.lastCheck = now;

    try {
      const reg = await navigator.serviceWorker?.getRegistration?.();
      await reg?.update();
    } catch {
      /* offline, or the browser declined: the content check below still decides */
    }

    let html: string;
    try {
      html = await (this.hooks.fetchText ?? ((u) => fetch(u, { cache: 'no-store' }).then((r) => r.text())))(
        `/index.html?u=${now}`,
      );
    } catch {
      return;   // offline: nothing to conclude
    }

    const latest = entryOf(html);
    const running = (this.hooks.runningEntry ?? defaultRunningEntry)();
    if (!latest || !running || latest === running) return;

    // A reload that keeps landing on the same stale bundle would loop for ever, so each version
    // gets a small, fixed number of attempts. Two, not one: the first reload can be served by
    // the OLD worker, which is still in control until the new one activates — suppressing the
    // second attempt was what left the page stale in testing.
    const attempts = readAttempts(latest);
    if (attempts >= MAX_ATTEMPTS) {
      this.ready = true;   // stop fighting the cache; let the person press the button
      return;
    }
    writeAttempts(latest, attempts + 1);
    await this.onNewVersion();
  }

  /** A newer version exists: reload now if that costs nothing, otherwise offer it. */
  async onNewVersion(): Promise<void> {
    if (this.hooks.isSignedIn()) {
      this.ready = true;
      return;
    }
    await this.apply();
  }

  /**
   * Reload — but only once the new worker is actually in control, or a moment has passed.
   * Reloading while the old worker still controls the page just serves the old shell again.
   */
  async apply(): Promise<void> {
    this.ready = false;
    try {
      const reg = await navigator.serviceWorker?.getRegistration?.();
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          navigator.serviceWorker.addEventListener('controllerchange', done, { once: true });
          setTimeout(done, CLAIM_WAIT_MS);
        });
      }
    } catch {
      /* no worker, or the browser declined: reload anyway, the server sends the new shell */
    }
    (this.hooks.reload ?? (() => location.reload()))();
  }

  dismiss(): void {
    this.ready = false;
  }

  dispose(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    this.booted = false;
  }
}

/** How many times this tab has already reloaded for a given version. */
function readAttempts(entry: string): number {
  try {
    const raw = sessionStorage.getItem(RELOADED_KEY);
    if (!raw) return 0;
    const v = JSON.parse(raw) as { entry?: string; n?: number };
    return v.entry === entry ? Number(v.n) || 0 : 0;
  } catch {
    return 0;
  }
}

function writeAttempts(entry: string, n: number): void {
  try {
    sessionStorage.setItem(RELOADED_KEY, JSON.stringify({ entry, n }));
  } catch {
    /* private mode: the worst case is one extra reload */
  }
}

export const appUpdate = new AppUpdate();
